import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { PulseLoader } from 'react-spinners';
import { useDispatch, useSelector } from 'react-redux';
import { setCounts } from '../store/statsSlice';

const API_URL = process.env.REACT_APP_API_URL;
const DASHBOARD_COUNTS_KEY = 'dashboard_counts';
const DASHBOARD_TIMESTAMP_KEY = 'dashboard_counts_timestamp';

function parseApiResponse(raw) {
  let data = raw;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return raw;
    }
  }
  if (data?.body != null) {
    if (typeof data.body === 'string') {
      try {
        return JSON.parse(data.body);
      } catch {
        return data.body;
      }
    }
    return data.body;
  }
  return data;
}

function formatCount(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString('en-IN');
}

const DASHBOARD_CARDS = [
  {
    key: 'supplier',
    title: 'Suppliers',
    subtitle: 'Total registered suppliers',
    icon: 'fa-solid fa-truck',
    colorClass: 'dashboard-box-teal',
    link: '/supplyMaster',
    linkLabel: 'View suppliers',
    permissionKey: 'supply_master',
  },
  {
    key: 'user',
    title: 'Users',
    subtitle: 'Active users',
    icon: 'fa-solid fa-users',
    colorClass: 'dashboard-box-navy',
    link: '/user',
    linkLabel: 'Manage users',
    permissionKey: 'user_management',
  },
  {
    key: 'material',
    title: 'Active Materials',
    subtitle: 'Materials currently active',
    icon: 'fa-solid fa-cubes',
    colorClass: 'dashboard-box-green',
    link: '/material',
    linkLabel: 'View materials',
    permissionKey: 'material_management',
  },
  {
    key: 'customer',
    title: 'Active Customers',
    subtitle: 'Customers currently active',
    icon: 'fa-solid fa-building',
    colorClass: 'dashboard-box-amber',
    link: '/customerMaster',
    linkLabel: 'View customers',
    permissionKey: 'customer_master',
  },
];
const SUPERADMIN_DASHBOARD_CARD = {
  key: 'businessAdmin',
  title: 'Business Admins',
  subtitle: 'Total business admins',
  icon: 'fa-solid fa-user-tie',
  colorClass: 'dashboard-box-navy',
  link: '/user',
  linkLabel: 'Manage business admins',
  permissionKey: 'user_management',
};

const DashboardStatBox = ({
  title,
  subtitle,
  value,
  icon,
  colorClass,
  link,
  linkLabel,
  isLoading,
  canNavigate,
}) => (
  <div className="col-12 col-sm-6 col-lg-3">
    <div className={`small-box dashboard-stat-box ${colorClass}`}>
      <div className="inner">
        <p className="dashboard-stat-label">{title}</p>
        {isLoading ? (
          <div className="dashboard-stat-loader">
            <PulseLoader color="#ffffff" size={8} />
          </div>
        ) : (
          <h3 className="dashboard-stat-value">{formatCount(value)}</h3>
        )}
        <p className="dashboard-stat-subtitle">{subtitle}</p>
      </div>
      <div className="icon">
        <i className={icon} />
      </div>
      {canNavigate ? (
        <Link to={link} className="small-box-footer">
          {linkLabel} <i className="fas fa-arrow-circle-right" />
        </Link>
      ) : (
        <span className="small-box-footer dashboard-stat-footer-disabled">
          Count overview
        </span>
      )}
    </div>
  </div>
);

const Dashboard = () => {
  const dispatch = useDispatch();
  const supplierCountFromStore = useSelector((state) => state.stats.supplierCount);
  const userCountFromStore = useSelector((state) => state.stats.userCount);
  const currentUser = useSelector((state) => state.user);

  const [supplierCount, setSupplierCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [activeMaterialCount, setActiveMaterialCount] = useState(0);
  const [activeCustomerCount, setActiveCustomerCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [businessAdminCount, setBusinessAdminCount] = useState(0);

  const userRole = currentUser?.role || localStorage.getItem('role');
  const isSuperAdmin =
  String(userRole || '').toLowerCase() === 'superadmin';
  const userPermissions = useMemo(() => {
    if (Array.isArray(currentUser?.permissions)) {
      return currentUser.permissions;
    }
    try {
      return JSON.parse(localStorage.getItem('permissions') || '[]');
    } catch {
      return [];
    }
  }, [currentUser?.permissions]);

  const hasPermission = (key) =>
    String(userRole || '').toLowerCase() === 'admin' ||
    userPermissions.includes(key);

  const userName = useMemo(() => {
    const raw =
      currentUser?.name ||
      localStorage.getItem('name') ||
      'there';
    const trimmed = String(raw).trim();
    if (!trimmed) return 'there';
    return trimmed
      .split(/\s+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }, [currentUser?.name]);

  const countByKey = {
    supplier: supplierCount,
    user: userCount,
    material: activeMaterialCount,
    customer: activeCustomerCount,
    businessAdmin: businessAdminCount,
  };

  useEffect(() => {
    if (supplierCountFromStore !== null) {
      setSupplierCount(supplierCountFromStore);
    }
    if (userCountFromStore !== null) {
      setUserCount(userCountFromStore);
    }

    const hadCachedCounts = loadCountsFromStorage();
    fetchCounts(!hadCachedCounts && supplierCountFromStore === null && userCountFromStore === null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (supplierCountFromStore !== null) {
      setSupplierCount(supplierCountFromStore);
      setIsLoading(false);
    }
  }, [supplierCountFromStore]);

  useEffect(() => {
    if (userCountFromStore !== null) {
      setUserCount(userCountFromStore);
      setIsLoading(false);
    }
  }, [userCountFromStore]);

  const loadCountsFromStorage = () => {
    try {
      const storedCounts = localStorage.getItem(DASHBOARD_COUNTS_KEY);
      if (storedCounts) {
        const counts = JSON.parse(storedCounts);
        let hasCounts = false;
        if (counts.supplierCount !== undefined) {
          setSupplierCount(counts.supplierCount);
          hasCounts = true;
        }
        if (counts.userCount !== undefined) {
          setUserCount(counts.userCount);
          hasCounts = true;
        }
        if (counts.activeMaterialCount !== undefined) {
          setActiveMaterialCount(counts.activeMaterialCount);
          hasCounts = true;
        }
        if (counts.activeCustomerCount !== undefined) {
          setActiveCustomerCount(counts.activeCustomerCount);
          hasCounts = true;
        }
        if (hasCounts) {
          dispatch(setCounts({
            supplierCount: counts.supplierCount,
            userCount: counts.userCount
          }));
          return true;
        }
      }
    } catch (error) {
      console.error("Error loading counts from localStorage:", error);
    }
    return false;
  };

  const saveCountsToStorage = (countsToSave) => {
    try {
      localStorage.setItem(DASHBOARD_COUNTS_KEY, JSON.stringify(countsToSave));
      localStorage.setItem(DASHBOARD_TIMESTAMP_KEY, new Date().toISOString());
    } catch (error) {
      console.error("Error saving counts to localStorage:", error);
    }
  };

  const hasCountsChanged = (nextCounts) => {
    try {
      const storedCounts = localStorage.getItem(DASHBOARD_COUNTS_KEY);
      if (!storedCounts) {
        return true;
      }
      const counts = JSON.parse(storedCounts);
      return (
        counts.supplierCount !== nextCounts.supplierCount ||
        counts.userCount !== nextCounts.userCount ||
        counts.activeMaterialCount !== nextCounts.activeMaterialCount ||
        counts.activeCustomerCount !== nextCounts.activeCustomerCount
      );
    } catch (error) {
      return true;
    }
  };

  const fetchCounts = async (showLoader = true) => {
    if (showLoader) {
      setIsLoading(true);
    }

    try {
      const [supplierResponse, userResponse, dashboardResponse] = await Promise.all([
        axios.post(API_URL, { action: "get-supplier-count" }),
        axios.post(API_URL, { action: "get-user-count" }),
        axios.post(API_URL, { action: "dashboard-counts" }),
      ]);

      const supplierData = parseApiResponse(supplierResponse.data);
      const userData = parseApiResponse(userResponse.data);
      const dashboardData = parseApiResponse(dashboardResponse.data);

      let apiSupplierCount = null;
      let apiUserCount = null;
      let apiActiveMaterialCount = null;
      let apiActiveCustomerCount = null;

      if (supplierData.success === true && supplierData.count !== undefined) {
        apiSupplierCount = supplierData.count;
      } else {
        console.error("Failed to fetch supplier count:", supplierData.message);
      }

      if (userData.success === true && userData.count !== undefined) {
        apiUserCount = userData.count;
      } else {
        console.error("Failed to fetch user count:", userData.message);
      }

      if (
  isSuperAdmin &&
  userData.success === true &&
  userData.count !== undefined
) {
  setBusinessAdminCount(userData.count);
}

      if (dashboardData.success === true && dashboardData.data) {
        apiActiveMaterialCount = Number(dashboardData.data.activeMaterialCount) || 0;
        apiActiveCustomerCount = Number(dashboardData.data.activeCustomerCount) || 0;
      } else {
        console.error("Failed to fetch dashboard counts:", dashboardData?.message);
      }

      if (apiSupplierCount !== null && apiUserCount !== null) {
        setSupplierCount(apiSupplierCount);
        setUserCount(apiUserCount);
        dispatch(setCounts({ supplierCount: apiSupplierCount, userCount: apiUserCount }));
      }

      if (apiActiveMaterialCount !== null) {
        setActiveMaterialCount(apiActiveMaterialCount);
      }
      if (apiActiveCustomerCount !== null) {
        setActiveCustomerCount(apiActiveCustomerCount);
      }

      if (
        apiSupplierCount !== null &&
        apiUserCount !== null &&
        apiActiveMaterialCount !== null &&
        apiActiveCustomerCount !== null
      ) {
        const nextCounts = {
          supplierCount: apiSupplierCount,
          userCount: apiUserCount,
          activeMaterialCount: apiActiveMaterialCount,
          activeCustomerCount: apiActiveCustomerCount,
        };
        if (hasCountsChanged(nextCounts)) {
          saveCountsToStorage(nextCounts);
        }
      }
    } catch (error) {
      console.error("Fetch counts error:", error);
      const hasCachedData = localStorage.getItem(DASHBOARD_COUNTS_KEY);
      if (!hasCachedData) {
        if (error.response && error.response.data) {
          let errorData = parseApiResponse(error.response.data);
          toast.error(errorData.message || "An error occurred while fetching counts", { autoClose: 2000 });
        } else {
          toast.error("Network error. Please check your connection and try again.", { autoClose: 2000 });
        }
      }
    } finally {
      if (showLoader) {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="dashboard-page">
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-sm-8">
              <h1 className="m-0 txt_lft dashboard-page-title">Dashboard</h1>
              <p className="dashboard-welcome mb-0">
                Welcome back, <span>{userName}</span> — here&apos;s your overview
              </p>
            </div>
            <div className="col-sm-4">
              <ol className="breadcrumb float-sm-right mb-0">
                <li className="breadcrumb-item active">Home</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
     {isSuperAdmin ? (
  <DashboardStatBox
    title={SUPERADMIN_DASHBOARD_CARD.title}
    subtitle={SUPERADMIN_DASHBOARD_CARD.subtitle}
    value={countByKey[SUPERADMIN_DASHBOARD_CARD.key]}
    icon={SUPERADMIN_DASHBOARD_CARD.icon}
    colorClass={SUPERADMIN_DASHBOARD_CARD.colorClass}
    link={SUPERADMIN_DASHBOARD_CARD.link}
    linkLabel={SUPERADMIN_DASHBOARD_CARD.linkLabel}
    isLoading={isLoading}
    canNavigate={hasPermission(SUPERADMIN_DASHBOARD_CARD.permissionKey)}
  />
) : (
  DASHBOARD_CARDS.map((card) => (
    <DashboardStatBox
      key={card.key}
      title={card.title}
      subtitle={card.subtitle}
      value={countByKey[card.key]}
      icon={card.icon}
      colorClass={card.colorClass}
      link={card.link}
      linkLabel={card.linkLabel}
      isLoading={isLoading}
      canNavigate={hasPermission(card.permissionKey)}
    />
  ))
)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
